import React from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { ArrowRight, ArrowLeft, Users, Building2, Target, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const AboutUs = ({ onNavigate, onBackToHome }) => {
  const { t } = useTranslation();

  const scrollToContact = () => {
    if (onNavigate) {
      onNavigate('contact');
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="about-us-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        <div className="container mx-auto px-6 relative z-10">
          {/* Back button */}
          <button
            onClick={onBackToHome}
            className="inline-flex items-center gap-2 text-slate-300 hover:text-white mb-8 transition-colors"
            data-testid="about-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('shop.backToHome', 'Back to Home')}
          </button>
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" data-testid="about-hero-title">
              {t('pages.aboutUs.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 leading-relaxed">
              {t('pages.aboutUs.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* The Challenge Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-slate-700 leading-relaxed mb-8">
                {t('pages.aboutUs.challenge.p1')}<br />
                {t('pages.aboutUs.challenge.p1b')}
              </p>
              
              <p className="text-xl text-slate-600 font-medium mb-6">
                {t('pages.aboutUs.challenge.missing')}
              </p>
              
              <div className="bg-slate-50 rounded-2xl p-8 mb-8 border-l-4 border-blue-500">
                <p className="text-slate-700 mb-3">{t('pages.aboutUs.challenge.strategies')}</p>
                <p className="text-slate-700 mb-3">{t('pages.aboutUs.challenge.insights')}</p>
                <p className="text-slate-700 mb-0">{t('pages.aboutUs.challenge.development')}</p>
              </div>
              
              <p className="text-xl text-slate-700 leading-relaxed mb-6">
                {t('pages.aboutUs.challenge.individuals')}
              </p>
              
              <p className="text-slate-600 leading-relaxed mb-8">
                {t('pages.aboutUs.challenge.individualsDesc')}
              </p>
              
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl p-8 mb-8">
                <p className="text-xl font-semibold mb-4">{t('pages.aboutUs.solution.title')}</p>
                <p className="text-blue-100 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('pages.aboutUs.solution.desc1')) }} />
                <p className="text-blue-100 leading-relaxed mb-4">
                  {t('pages.aboutUs.solution.desc2')}
                </p>
                <p className="text-white font-medium text-lg">
                  {t('pages.aboutUs.solution.motto1')}<br />
                  {t('pages.aboutUs.solution.motto2')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Organizations Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{t('pages.aboutUs.forOrgs.title')}</h2>
            </div>
            
            <p className="text-xl text-slate-700 leading-relaxed mb-8">
              {t('pages.aboutUs.forOrgs.desc')}
            </p>
            
            <p className="text-lg text-slate-600 mb-6">{t('pages.aboutUs.forOrgs.enables')}</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {['item1', 'item2', 'item3', 'item4'].map((key, idx) => (
                <div key={`org-${key}`} className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{t(`pages.aboutUs.forOrgs.${key}`)}</span>
                </div>
              ))}
            </div>
            
            <p className="text-slate-600 italic">
              {t('pages.aboutUs.forOrgs.footer')}
            </p>
          </div>
        </div>
      </section>

      {/* For Individuals Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{t('pages.aboutUs.forIndividuals.title')}</h2>
            </div>
            
            <p className="text-xl text-slate-700 leading-relaxed mb-8">
              {t('pages.aboutUs.forIndividuals.desc')}
            </p>
            
            <p className="text-lg text-slate-600 mb-6">{t('pages.aboutUs.forIndividuals.enables')}</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {['item1', 'item2', 'item3', 'item4'].map((key, idx) => (
                <div key={`ind-${key}`} className="flex items-start gap-3 bg-emerald-50 p-4 rounded-xl">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{t(`pages.aboutUs.forIndividuals.${key}`)}</span>
                </div>
              ))}
            </div>
            
            <p className="text-slate-600 italic">
              {t('pages.aboutUs.forIndividuals.footer')}
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">{t('pages.aboutUs.approach.title')}</h2>
            </div>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              {t('pages.aboutUs.approach.desc')}<br />
              <span className="text-white font-semibold">{t('pages.aboutUs.approach.balance')}</span>
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <h4 className="font-semibold text-lg mb-3">{t('pages.aboutUs.approach.individual.title')}</h4>
                <p className="text-slate-400">
                  {t('pages.aboutUs.approach.individual.desc')}
                </p>
              </div>
              <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <h4 className="font-semibold text-lg mb-3">{t('pages.aboutUs.approach.organization.title')}</h4>
                <p className="text-slate-400">
                  {t('pages.aboutUs.approach.organization.desc')}
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-600/20 to-indigo-600/20 rounded-2xl p-8 border border-blue-500/30">
              <p className="text-slate-200 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('pages.aboutUs.approach.fiveD')) }} />
              <p className="text-slate-300">
                {t('pages.aboutUs.approach.fiveDSub')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-amber-500 rounded-2xl flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{t('pages.aboutUs.different.title')}</h2>
            </div>
            
            <p className="text-xl text-slate-700 leading-relaxed mb-8">
              {t('pages.aboutUs.different.desc')}
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {[
                { key: 'human', color: 'bg-rose-500' },
                { key: 'structure', color: 'bg-blue-500' },
                { key: 'measurement', color: 'bg-emerald-500' }
              ].map((item, idx) => (
                <div key={item.key} className="text-center">
                  <div className={`w-16 h-16 ${item.color} rounded-2xl mx-auto mb-4 flex items-center justify-center`}>
                    <span className="text-2xl font-bold text-white">{idx + 1}</span>
                  </div>
                  <h4 className="font-semibold text-lg text-slate-900">{t(`pages.aboutUs.different.${item.key}.title`)}</h4>
                  <p className="text-slate-600">{t(`pages.aboutUs.different.${item.key}.desc`)}</p>
                </div>
              ))}
            </div>
            
            <p className="text-slate-600 leading-relaxed">
              {t('pages.aboutUs.different.footer')}
            </p>
          </div>
        </div>
      </section>

      {/* Our Perspective Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">{t('pages.aboutUs.perspective.title')}</h2>
            
            <p className="text-xl text-slate-700 leading-relaxed mb-6">
              {t('pages.aboutUs.perspective.p1')}<br />
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('pages.aboutUs.perspective.p1b')) }} />
            </p>
            
            <p className="text-lg text-slate-600 mb-8">
              {t('pages.aboutUs.perspective.p2')}<br />
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('pages.aboutUs.perspective.p2b')) }} />
            </p>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
              <p className="text-xl text-blue-600 font-medium mb-4">
                {t('pages.aboutUs.perspective.shift')}
              </p>
              <p className="text-slate-600 leading-relaxed">
                {t('pages.aboutUs.perspective.impact1')}<br />
                {t('pages.aboutUs.perspective.impact2')}
              </p>
              <p className="text-slate-800 font-medium mt-4 text-lg">
                {t('pages.aboutUs.perspective.impact3')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xl text-blue-100 mb-6">
              {t('pages.aboutUs.cta.desc')}
            </p>
            <p className="text-2xl font-semibold mb-8">
              {t('pages.aboutUs.cta.title')}
            </p>
            <Button 
              onClick={scrollToContact}
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
              data-testid="about-cta-btn"
            >
              {t('pages.aboutUs.cta.button')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
